import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { createUploadPlaceholder, validateMediaUploadFile } from './mediaAttachments'

export const CLIENT_UPLOAD_PHASES = new Set(['queued', 'starting', 'transferring', 'reconnecting', 'preparing', 'analysis_starting'])
export const UNFINISHED_UPLOAD_PHASES = new Set([...CLIENT_UPLOAD_PHASES, 'failed', 'canceled', 'interrupted'])

export function hasActiveMediaUploads(media) {
  return media.some((item) => item.uploading)
}

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:')
}

export function hasUnfinishedMediaUploads(media) {
  return media.some((item) => item.uploading || UNFINISHED_UPLOAD_PHASES.has(item.uploadPhase))
}

export function restoreUploadPlaceholder(media) {
  return {
    ...media,
    id: media.id || crypto.randomUUID(),
    previewUrl: null,
    url: null,
    uploading: false,
    uploadPhase: 'interrupted',
  }
}

// Server-side follow-up: idempotent completion or an orphan sweep is still
// needed for the narrow case where completion commits but never returns an id.
export function useMediaUploadQueue(setUploadedMedia, { keepOnUnmount = false, onPersistedWhileUnmounted } = {}) {
  const tasks = useRef(new Map())
  const queue = useRef([])
  const running = useRef(false)
  const mounted = useRef(true)
  const persistedWhileUnmountedRef = useRef(onPersistedWhileUnmounted)
  persistedWhileUnmountedRef.current = onPersistedWhileUnmounted

  useEffect(() => {
    mounted.current = true
    const activeTasks = tasks.current
    return () => {
      mounted.current = false
      for (const task of activeTasks.values()) {
        const preserveFinalization = keepOnUnmount && task.transferComplete
        if (preserveFinalization) {
          task.keepOnUnmount = true
        } else {
          task.controller?.abort()
          activeTasks.delete(task.id)
        }
      }
      queue.current = []
    }
  }, [keepOnUnmount])

  const update = useCallback((id, changes) => {
    if (!mounted.current) return
    setUploadedMedia((current) => current.map((media) => media.id === id ? { ...media, ...changes } : media))
  }, [setUploadedMedia])

  const drain = useCallback(async () => {
    if (running.current) return
    running.current = true
    try {
      while (mounted.current && queue.current.length) {
        const task = tasks.current.get(queue.current.shift())
        if (!task || task.status !== 'queued') continue
        task.status = 'running'
        task.controller = new AbortController()
        update(task.id, { uploading: true, uploadPhase: 'starting', uploadBytes: null, uploadError: '', uploadStartedAt: Date.now() })
        try {
          const asset = task.asset || await task.actions.upload(task.file, task.mediaType, {
            signal: task.controller.signal,
            session: task.session,
            transferComplete: task.transferComplete,
            onSession: (session) => { task.session = session },
            onStorageSession: (url) => { task.session = { ...task.session, resume_url: url } },
            onProgress: (uploadProgress) => update(task.id, { uploadProgress }),
            onBytesProgress: (bytes) => update(task.id, { uploadBytes: bytes, uploadPhase: 'transferring' }),
            onPhase: (uploadPhase) => {
              if (uploadPhase === 'preparing') task.transferComplete = true
              update(task.id, { uploadPhase })
            },
          })
          const preserveFinalization = task.keepOnUnmount || (keepOnUnmount && task.transferComplete && !mounted.current)
          if (task.controller.signal.aborted || (!mounted.current && !preserveFinalization) || !tasks.current.has(task.id)) {
            if (asset?.id && !task.asset && !preserveFinalization) task.actions.discard?.(asset)
            continue
          }
          if (!asset?.id || !asset?.url) throw new Error('Missing uploaded media')
          task.asset = asset
          if (mounted.current) task.actions.attach(task.id, task.mediaType, task.file, asset)
          else if (preserveFinalization) persistedWhileUnmountedRef.current?.(asset)
          if (task.mediaType === 'video' && task.actions.analyze) {
            update(task.id, { uploadPhase: 'analysis_starting' })
            await task.actions.analyze(task.id, task.file, asset)
          }
          if (task.controller.signal.aborted || (!mounted.current && !preserveFinalization)) continue
          if (mounted.current) update(task.id, { uploading: false, uploadPhase: 'ready' })
          tasks.current.delete(task.id)
        } catch (error) {
          if (!mounted.current || !tasks.current.has(task.id)) continue
          if (task.status === 'queued') continue
          const canceled = task.controller.signal.aborted || error?.name === 'AbortError'
          const responseData = error?.response?.data || {}
          const detail = responseData.error
          const code = responseData.code || detail?.code
          const invalidFile = ['unsupported_media', 'invalid_media', 'file_too_large', 'media_too_large', 'empty_file'].includes(code)
          if (error?.nonRetryable || ['upload_expired', 'expired', 'failed'].includes(code)) {
            task.session = null
            task.transferComplete = false
          }
          if (code === 'upload_incomplete') task.transferComplete = false
          task.status = canceled ? 'canceled' : 'failed'
          update(task.id, {
            uploading: false,
            uploadPhase: task.status,
            uploadCanRetry: !invalidFile,
            uploadError: canceled ? '' : invalidFile ? (typeof detail === 'string' ? detail : detail?.message || 'Choose a different file to upload.') : task.transferComplete
              ? 'Your file was transferred, but media preparation could not finish. Retry to check it again.'
              : 'Upload could not finish. Check your connection and retry.',
          })
        }
      }
    } finally {
      running.current = false
    }
  }, [keepOnUnmount, update])

  const enqueue = useCallback((files, actions) => {
    const placeholders = []
    for (const file of files) {
      const validationError = validateMediaUploadFile(file)
      if (validationError) {
        toast.error(`Upload failed: ${validationError}`)
        continue
      }
      const { placeholderId: id, mediaType, placeholder } = createUploadPlaceholder(file)
      tasks.current.set(id, { id, file, mediaType, actions, status: 'queued', previewUrl: placeholder.previewUrl })
      queue.current.push(id)
      placeholders.push({ ...placeholder, uploadPhase: 'queued', uploadSize: file.size })
    }
    setUploadedMedia((current) => [...current, ...placeholders])
    void drain()
  }, [drain, setUploadedMedia])

  const retry = useCallback((id) => {
    const task = tasks.current.get(id)
    if (!task || !['failed', 'canceled'].includes(task.status)) return
    task.status = 'queued'
    update(id, { uploading: true, uploadPhase: 'queued', uploadError: '' })
    queue.current.push(id)
    void drain()
  }, [drain, update])

  const cancel = useCallback((id) => {
    const task = tasks.current.get(id)
    if (!task) return
    task.status = 'canceled'
    task.controller?.abort()
    update(id, { uploading: false, uploadPhase: 'canceled', uploadError: '' })
  }, [update])

  const remove = useCallback((id) => {
    tasks.current.get(id)?.controller?.abort()
    tasks.current.delete(id)
  }, [])

  const dropPendingUpload = useCallback((id) => {
    const task = tasks.current.get(id)
    const previewUrl = task?.previewUrl
    if (isBlobUrl(previewUrl)) URL.revokeObjectURL(previewUrl)
    task?.controller?.abort()
    tasks.current.delete(id)
    queue.current = queue.current.filter((queuedId) => queuedId !== id)
    setUploadedMedia((current) => current.filter((media) => media.id !== id))
  }, [setUploadedMedia])

  return { enqueue, retry, cancel, remove, dropPendingUpload }
}
